/**
 * Everything that went wrong and had nowhere to be said.
 *
 * A renderer fails quietly. A shader that will not link, a context the browser took back, a
 * capability that was advertised and does not work: three logs some of it to a console nobody on a
 * phone can open, and the rest is a black canvas under a live HUD with no message anywhere. That is
 * the state this game shipped in on iOS Safari, and it is why finding the cause needed a desktop
 * reproduction rather than a bug report.
 *
 * So this is a ring buffer, and it is the one piece of the diagnostics that is not lazily loaded.
 * It has to be in the initial chunk because most of what it records happens before anybody could
 * ask for it: the surface is built, the chain is built, and both can fail while the menu is still
 * painting. It imports nothing and holds a few dozen strings, which is what that costs.
 *
 * Reading it is `?diag=1`, which loads the overlay in `diagOverlay.ts`.
 * `tools/bin/render-probe.mjs` reads the same list off the page, so the tool and the phone see one
 * record rather than two.
 */

/** What a fault is about. Enough to group the list, not a taxonomy. */
export type FaultArea = 'render' | 'context' | 'shader' | 'capability';

export interface Fault {
  readonly area: FaultArea;
  readonly message: string;
  /** Milliseconds since the page opened, so an order can be read off the list. */
  readonly at: number;
}

/**
 * Enough to hold a session's worth of trouble without ever growing. A run that produces more than
 * this is a run where the first ones are the interesting ones, so the oldest are kept.
 */
const CAPACITY = 64;

const faults: Fault[] = [];

/** The name the probe tool and the overlay both read the list from. */
const GLOBAL_KEY = '__tbdFaults';

export function reportFault(area: FaultArea, message: string): void {
  if (faults.length >= CAPACITY) return;
  faults.push({ area, message, at: Math.round(performance.now()) });
}

export function recordedFaults(): readonly Fault[] {
  return faults;
}

/**
 * Hangs the list off the window so a browser driving the page can read it without the overlay.
 * Called once from the entry, because a module that reaches for a global on import is a module
 * that cannot be tested.
 */
export function publishFaults(): void {
  Object.defineProperty(window, GLOBAL_KEY, { value: recordedFaults, configurable: true });
}
